import React, { useState } from 'react';
import { Trash2, AlertTriangle, Search, Filter, Download, RotateCcw, X, Plus, Eye } from 'lucide-react';
import {
  useMassDeleteJobs,
  useCreateMassDeleteJob,
  useCancelMassDeleteJob,
  useRestoreDeletedRecords,
  useDeletePreview,
  formatFilter,
  getStatusColor,
  getDeleteDuration,
  type DeleteFilter,
  type MassDeleteJob
} from '../../services/queries/massDeleteQueries';
import { useObjects } from '../../services/queries/objectQueries';
import { useFieldsByObject } from '../../services/queries/fieldQueries';
import { useToast } from '../../contexts/ToastContext';
import {
  StandardLoadingState,
  StandardEmptyState
} from '../standard';

export const MassDeleteManager: React.FC = () => {
  const { success, error: showError } = useToast();

  const [activeTab, setActiveTab] = useState<'delete' | 'history'>('delete');
  const [selectedObject, setSelectedObject] = useState<string>('');
  const [filters, setFilters] = useState<DeleteFilter[]>([]);
  const [filterLogic, setFilterLogic] = useState<'AND' | 'OR'>('AND');
  const [deleteType, setDeleteType] = useState<'hard' | 'soft'>('soft');
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const { data: objects } = useObjects();
  const { data: deleteJobs, isLoading } = useMassDeleteJobs();
  const { data: preview, isLoading: previewLoading } = useDeletePreview(
    selectedObject,
    filters,
    showPreview && selectedObject !== ''
  );

  const createJobMutation = useCreateMassDeleteJob();
  const cancelJobMutation = useCancelMassDeleteJob();
  const restoreMutation = useRestoreDeletedRecords();

  const selectedObjectData = objects?.find((o: any) => o.apiName === selectedObject);
  const { data: fieldData } = useFieldsByObject(selectedObjectData?.id || '');
  const availableFields = fieldData?.map((f: any) => ({
    apiName: f.fieldName,
    label: f.fieldLabel,
    type: f.fieldType,
    options: f.picklistValues?.map((v: string) => ({ value: v, label: v }))
  })) || [];

  const handleAddFilter = () => {
    const firstField = availableFields[0];
    if (!firstField) return;

    const newFilter: DeleteFilter = {
      id: `filter_${Date.now()}`,
      field: firstField.apiName,
      fieldLabel: firstField.label,
      operator: 'equals',
      value: ''
    };

    setFilters([...filters, newFilter]);
  };

  const handleUpdateFilter = (filterId: string, updates: Partial<DeleteFilter>) => {
    setFilters(filters.map(f => {
      if (f.id === filterId) {
        // If field changed, reset value
        if (updates.field) {
          const field = availableFields.find((af: any) => af.apiName === updates.field);
          if (field) {
            return {
              ...f,
              field: field.apiName,
              fieldLabel: field.label,
              value: ''
            };
          }
        }
        return { ...f, ...updates };
      }
      return f;
    }));
  };

  const handleRemoveFilter = (filterId: string) => {
    setFilters(filters.filter(f => f.id !== filterId));
  };

  const handlePreview = () => {
    if (!selectedObject) {
      showError('No object selected', 'Please select an object');
      return;
    }
    if (filters.length === 0) {
      showError('No filters configured', 'Please add at least one filter');
      return;
    }
    setShowPreview(true);
  };

  const handleDelete = async () => {
    if (!preview) return;

    const expectedText = `DELETE ${preview.totalMatching} RECORDS`;
    if (confirmText !== expectedText) {
      showError('Confirmation text incorrect', `Please type "${expectedText}" to confirm`);
      return;
    }

    if (!reason.trim()) {
      showError('Reason required', 'Please provide a reason for this deletion');
      return;
    }

    try {
      await createJobMutation.mutateAsync({
        objectName: selectedObject,
        objectLabel: selectedObjectData?.label || selectedObject,
        filters,
        filterLogic,
        deleteType,
        totalRecords: preview.totalMatching,
        reason
      });

      // Reset form
      setFilters([]);
      setReason('');
      setConfirmText('');
      setShowPreview(false);
      setActiveTab('history');

      success('Mass delete job started successfully!');
    } catch (err: any) {
      showError('Failed to start delete job', err.message || 'Please try again');
    }
  };

  const handleCancel = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to cancel this delete job?')) return;

    try {
      await cancelJobMutation.mutateAsync(jobId);
      success('Delete job cancelled successfully!');
    } catch (err: any) {
      showError('Failed to cancel job', err.message || 'Please try again');
    }
  };

  const handleRestore = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to restore these deleted records?')) return;

    try {
      const result = await restoreMutation.mutateAsync(jobId);
      success('Records restored successfully!', `Restored ${result.restoredRecords} records`);
    } catch (err: any) {
      showError('Failed to restore records', err.message || 'Please try again');
    }
  };

  const handleReset = () => {
    setFilters([]);
    setShowPreview(false);
    setConfirmText('');
    setReason('');
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Mass Delete Records
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Bulk delete records with filters, preview, and audit trail
        </p>
      </div>

      {/* Development Mode Banner */}
      <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300">
          <span>ℹ️</span>
          <span>
            <strong>Development Mode:</strong> Showing mock data from <code className="px-1 py-0.5 bg-blue-100 dark:bg-blue-900 rounded">massDeleteQueries.ts</code>
            {deleteJobs && ` • ${deleteJobs.length} delete jobs`}
          </span>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
              Warning: Mass Delete Operations
            </p>
            <p className="text-sm text-red-700 dark:text-red-400">
              Hard deletes are permanent and cannot be undone. Use soft delete to allow record restoration.
              Always preview records before deletion and provide a detailed reason for audit purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('delete')}
            className={`pb-3 px-2 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'delete'
                ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Delete Records
            </div>
          </button>

          <button
            onClick={() => setActiveTab('history')}
            className={`pb-3 px-2 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'history'
                ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Delete History
              {deleteJobs && deleteJobs.length > 0 && (
                <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full">
                  {deleteJobs.length}
                </span>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Delete Tab */}
      {activeTab === 'delete' && (
        <div className="space-y-6">
          {/* Step 1: Select Object */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Step 1: Select Object
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Object to Delete From <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedObject}
                onChange={(e) => {
                  setSelectedObject(e.target.value);
                  setFilters([]);
                  setShowPreview(false);
                }}
                className="w-full md:w-96 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select an object...</option>
                {objects?.map((obj: any) => (
                  <option key={obj.id} value={obj.apiName}>
                    {obj.label} ({obj.apiName})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Step 2: Define Filters */}
          {selectedObject && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Step 2: Define Filters ({filters.length})
                </h3>
                <button
                  onClick={handleAddFilter}
                  className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Filter
                </button>
              </div>

              {filters.length > 1 && (
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Logic:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setFilterLogic('AND')}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        filterLogic === 'AND'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      AND
                    </button>
                    <button
                      onClick={() => setFilterLogic('OR')}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        filterLogic === 'OR'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      OR
                    </button>
                  </div>
                </div>
              )}

              {filters.length > 0 ? (
                <div className="space-y-3">
                  {filters.map((filter, index) => (
                    <FilterRow
                      key={filter.id}
                      filter={filter}
                      availableFields={availableFields}
                      showLogic={index > 0}
                      logic={filterLogic}
                      onUpdate={(updates) => handleUpdateFilter(filter.id, updates)}
                      onRemove={() => handleRemoveFilter(filter.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                  <Filter className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">No filters defined</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                    Add filters to specify which records to delete
                  </p>
                  <button
                    onClick={handleAddFilter}
                    className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 text-sm font-medium"
                  >
                    + Add First Filter
                  </button>
                </div>
              )}

              {filters.length > 0 && (
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={handlePreview}
                    disabled={previewLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    {previewLoading ? 'Loading Preview...' : 'Preview Records'}
                  </button>

                  <button
                    onClick={handleReset}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Preview & Confirm */}
          {showPreview && preview && (
            <>
              {/* Preview */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Step 3: Preview Records ({preview.totalMatching} matching)
                </h3>

                {/* Warnings */}
                {preview.warnings.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {preview.warnings.map((warning: string, index: number) => (
                      <div key={index} className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-yellow-800 dark:text-yellow-300">{warning}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Record Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Last Activity
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {preview.records.slice(0, 10).map((record: any) => (
                        <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 font-mono">
                            {record.id}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {record.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {record.status}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {record.lastActivity}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {preview.totalMatching > 10 && (
                    <div className="mt-3 text-center text-sm text-gray-500 dark:text-gray-400">
                      Showing 10 of {preview.totalMatching} records
                    </div>
                  )}
                </div>
              </div>

              {/* Delete Options */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Step 4: Delete Options
                </h3>

                <div className="space-y-4">
                  {/* Delete Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Delete Type
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <input
                          type="radio"
                          checked={deleteType === 'soft'}
                          onChange={() => setDeleteType('soft')}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-white">
                            Soft Delete (Recommended)
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            Mark records as deleted. Can be restored later.
                          </div>
                        </div>
                      </label>

                      <label className="flex items-start gap-3 p-3 border-2 border-red-200 dark:border-red-800 rounded-lg cursor-pointer transition-colors hover:bg-red-50 dark:hover:bg-red-900/20">
                        <input
                          type="radio"
                          checked={deleteType === 'hard'}
                          onChange={() => setDeleteType('hard')}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-red-900 dark:text-red-300">
                            Hard Delete (Permanent)
                          </div>
                          <div className="text-sm text-red-700 dark:text-red-400">
                            Permanently remove records. Cannot be restored.
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reason for Deletion <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Provide a detailed reason for this deletion..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {/* Confirmation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Confirmation <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Type <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded font-mono">DELETE {preview.totalMatching} RECORDS</code> to confirm
                    </p>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={`DELETE ${preview.totalMatching} RECORDS`}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleDelete}
                      disabled={
                        confirmText !== `DELETE ${preview.totalMatching} RECORDS` ||
                        !reason.trim() ||
                        createJobMutation.isPending
                      }
                      className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
                    >
                      <Trash2 className="w-4 h-4" />
                      {createJobMutation.isPending ? 'Starting Delete...' : `Delete ${preview.totalMatching} Records`}
                    </button>

                    <button
                      onClick={() => {
                        setShowPreview(false);
                        setConfirmText('');
                      }}
                      className="px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          {isLoading ? (
            <StandardLoadingState message="Loading delete history..." />
          ) : deleteJobs && deleteJobs.length > 0 ? (
            <div className="space-y-3">
              {deleteJobs.map((job: any) => (
                <DeleteJobCard
                  key={job.id}
                  job={job}
                  onCancel={handleCancel}
                  onRestore={handleRestore}
                />
              ))}
            </div>
          ) : (
            <StandardEmptyState
              icon={<Trash2 className="w-16 h-16" />}
              title="No delete history"
              message="Mass delete operations will appear here"
            />
          )}
        </div>
      )}
    </div>
  );
};

// Filter Row Component
interface FilterRowProps {
  filter: DeleteFilter;
  availableFields: any[];
  showLogic: boolean;
  logic: 'AND' | 'OR';
  onUpdate: (updates: Partial<DeleteFilter>) => void;
  onRemove: () => void;
}

const FilterRow: React.FC<FilterRowProps> = ({
  filter,
  availableFields,
  showLogic,
  logic,
  onUpdate,
  onRemove
}) => {
  const selectedField = availableFields.find(f => f.apiName === filter.field);
  const needsValue = !['isNull', 'isNotNull'].includes(filter.operator);

  const operators = [
    { value: 'equals', label: 'equals' },
    { value: 'notEquals', label: 'does not equal' },
    { value: 'lessThan', label: 'less than' },
    { value: 'greaterThan', label: 'greater than' },
    { value: 'contains', label: 'contains' },
    { value: 'startsWith', label: 'starts with' },
    { value: 'isNull', label: 'is blank' },
    { value: 'isNotNull', label: 'is not blank' }
  ];

  return (
    <div className="space-y-2">
      {showLogic && (
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className={`text-xs font-bold px-3 py-1 rounded ${
            logic === 'AND'
              ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
              : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
          }`}>
            {logic}
          </span>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>
      )}

      <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Field Selector */}
        <div className="flex-1">
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Field</label>
          <select
            value={filter.field}
            onChange={(e) => onUpdate({ field: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {availableFields.map(field => (
              <option key={field.apiName} value={field.apiName}>
                {field.label}
              </option>
            ))}
          </select>
        </div>

        {/* Operator Selector */}
        <div className="flex-1">
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Operator</label>
          <select
            value={filter.operator}
            onChange={(e) => onUpdate({ operator: e.target.value as any })}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {operators.map(op => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
        </div>

        {/* Value Input */}
        {needsValue && (
          <div className="flex-1">
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Value</label>
            {selectedField?.type === 'checkbox' ? (
              <select
                value={String(filter.value)}
                onChange={(e) => onUpdate({ value: e.target.value === 'true' })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            ) : selectedField?.type === 'picklist' ? (
              <select
                value={String(filter.value)}
                onChange={(e) => onUpdate({ value: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select value...</option>
                {selectedField.options?.map((opt: any) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={selectedField?.type === 'number' || selectedField?.type === 'currency' ? 'number' : selectedField?.type === 'date' ? 'date' : 'text'}
                value={String(filter.value)}
                onChange={(e) => onUpdate({ value: e.target.value })}
                placeholder="Enter value..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            )}
          </div>
        )}

        {/* Remove Button */}
        <div className={needsValue ? 'pt-6' : 'pt-6'}>
          <button
            onClick={onRemove}
            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
            title="Remove filter"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete Job Card Component
interface DeleteJobCardProps {
  job: MassDeleteJob;
  onCancel: (jobId: string) => void;
  onRestore: (jobId: string) => void;
}

const DeleteJobCard: React.FC<DeleteJobCardProps> = ({ job, onCancel, onRestore }) => {
  const statusColors: Record<string, string> = {
    preview: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    pending: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400',
    'in-progress': 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
    completed: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400',
    failed: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400',
    cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
  };

  const canCancel = job.status === 'pending' || job.status === 'in-progress';
  const canRestore = job.status === 'completed' && job.deleteType === 'soft';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {job.objectLabel}
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {job.jobNumber}
            </span>
            <span className={`px-2 py-0.5 text-xs rounded font-medium ${statusColors[job.status]}`}>
              {job.status.charAt(0).toUpperCase() + job.status.slice(1).replace('-', ' ')}
            </span>
            <span className={`px-2 py-0.5 text-xs rounded font-medium ${
              job.deleteType === 'soft'
                ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
            }`}>
              {job.deleteType === 'soft' ? 'Soft Delete' : 'Hard Delete'}
            </span>
          </div>

          {/* Filters */}
          <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
            <strong>Filters:</strong> {job.filters.map(f => formatFilter(f)).join(` ${job.filterLogic} `)}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total:</span>
              <span className="ml-1 font-semibold text-gray-900 dark:text-white">
                {job.totalRecords}
              </span>
            </div>
            {job.status === 'completed' && (
              <>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Deleted:</span>
                  <span className="ml-1 font-semibold text-green-600 dark:text-green-400">
                    {job.deletedRecords}
                  </span>
                </div>
                {job.failedRecords > 0 && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Failed:</span>
                    <span className="ml-1 font-semibold text-red-600 dark:text-red-400">
                      {job.failedRecords}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Duration:</span>
                  <span className="ml-1 font-semibold text-gray-900 dark:text-white">
                    {getDeleteDuration(job)}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Reason */}
          {job.reason && (
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              <strong>Reason:</strong> {job.reason}
            </div>
          )}

          {/* Metadata */}
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Created {new Date(job.createdDate).toLocaleString()} by {job.createdBy}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-4">
          {canCancel && (
            <button
              onClick={() => onCancel(job.id)}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Cancel
            </button>
          )}

          {canRestore && (
            <button
              onClick={() => onRestore(job.id)}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Restore
            </button>
          )}

          <button className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1">
            <Download className="w-3 h-3" />
            Log
          </button>
        </div>
      </div>
    </div>
  );
};
